/**
 * Option and selection types consumed by the interactive selector components
 * (style tiles, colour selector, icon tiles, review method) and by the chat
 * domain (message union, Claude parser, useChat hook).
 *
 * These live in `types/` rather than `components/` so non-UI modules can depend
 * on them without inverting the dependency direction.
 */

export interface StyleOption {
    value: string;
    name: string;
    description: string;
    imageUrl: string;
}

export interface ColorOption {
    value: string;
    name: string;
    imageUrl: string;
}

export interface TileChoiceOption {
    value: string;
    name: string;
    icon: 'online' | 'video' | 'business' | 'personal';
}

export const REVIEW_METHOD = {
    Standard: 'Standard',
    DesignLive: 'DesignLive',
} as const;

export type ReviewMethod = (typeof REVIEW_METHOD)[keyof typeof REVIEW_METHOD];

export interface ReviewMethodOption {
    value: ReviewMethod;
    name: string;
}

export interface ReviewMethodData {
    title?: string;
    options: ReviewMethodOption[];
    preselectedMethod?: ReviewMethod;
    /** Optional: pre-select the appointment date for step 2 (DesignLive only). Format: YYYY-MM-DD. */
    preselectedDate?: string;
    /** Optional: pre-select the appointment time for step 2 (DesignLive only). */
    preselectedTime?: string;
}

export interface ReviewMethodSelection {
    method: ReviewMethod;
    scheduledDate?: string;
    scheduledTime?: string;
    timezone?: string;
}

/**
 * An image successfully uploaded to MCP via useFileUploader. `mediaType` is
 * captured at upload time from File.type since UploadedFile from the hook does
 * not include it.
 *
 * `base64` and `compressedMediaType` are populated at pick-time (from
 * compressImage) so the full image payload can be sent to Claude. They are
 * absent when an attachment is reconstructed from server-side history.
 */
export interface UploadedAttachment {
    uploadId: string;
    previewUrl: string;
    downloadUrl: string;
    fileName: string;
    mediaType: string;
    /** Original file size as a string (matches useFileUploader's UploadedFile). */
    fileSize?: string;
    /**
     * Base64 string (no data-URL prefix) of the image sent to Claude.
     * Populated at pick-time via `compressImage` (JPEG) or `readFileAsBase64`
     * (original format fallback); absent when hydrated from session history.
     */
    base64?: string;
    /** MIME type of the `base64` payload — 'image/jpeg' when compressed, otherwise the original file type. */
    compressedMediaType?: string;
}

/* ─── Product Options types ─────────────────────────────────────────────── */

export interface ProductOptionValue {
    key: string;
    name: string;
    isCompatible: boolean;
    imageUrl?: string;
}

export interface ProductOptionItem {
    key: string;
    name: string;
    summaryName: string;
    values: ProductOptionValue[];
}

/** Data payload sent by the backend for the product-options component. */
export interface ProductOptionsData {
    title?: string;
    mpvId: string;
    tenant: string;
    /** Populated by the component via GraphQL; absent in the initial payload. */
    options?: ProductOptionItem[];
    /** Optional: pre-select the step-1 choice when the LLM already knows the user's intent. */
    preselectedChoice?: ProductOptionsChoice;
    /** Optional: pre-select specific product option values for step 2 (CUSTOMER flow).
     * Keys are option attribute keys (e.g. "Shape"), values are value keys (e.g. "Square").
     * Implies preselectedChoice === CUSTOMER; component auto-advances to step 2.
     * Unrecognised keys/values are silently ignored. */
    preselectedSelections?: Record<string, string>;
}

/** User's selection result from the ProductOptionsSelector wizard. */
export const PRODUCT_OPTIONS_CHOICE_CUSTOMER = 'CUSTOMER' as const;
export const PRODUCT_OPTIONS_CHOICE_DESIGNER = 'DESIGNER' as const;
export type ProductOptionsChoice =
    | typeof PRODUCT_OPTIONS_CHOICE_CUSTOMER
    | typeof PRODUCT_OPTIONS_CHOICE_DESIGNER;

export interface ProductOptionsSelection {
    choice: ProductOptionsChoice;
    /** Key→value pairs of selected product options (only present for CUSTOMER). */
    selections?: Record<string, string>;
    /** Display-friendly details for the message view (only present for CUSTOMER). */
    selectionDetails?: Array<{
        optionName: string;
        valueName: string;
        imageUrl?: string;
    }>;
}

export const REFERENCE_PROJECTS_DECISION_WITHOUT = 'WITHOUT_REFERENCE_PROJECTS' as const;
export const REFERENCE_PROJECTS_DECISION_WITH = 'WITH_REFERENCE_PROJECTS' as const;
export type ReferenceProjectsDecision =
    | typeof REFERENCE_PROJECTS_DECISION_WITHOUT
    | typeof REFERENCE_PROJECTS_DECISION_WITH;

/** Data payload sent by the backend for the reference-projects component. */
export interface ReferenceProjectsData {
    title?: string;
    /** Optional: pre-select the step-1 decision when the LLM already knows the user's intent. */
    preselectedDecision?: ReferenceProjectsDecision;
    /** Optional: pre-select specific work IDs in the project picker (implies WITH decision). */
    preselectedWorkIds?: string[];
}

export type ReferenceProjectsResult =
    | { decision: typeof REFERENCE_PROJECTS_DECISION_WITHOUT }
    | { decision: typeof REFERENCE_PROJECTS_DECISION_WITH; workIds: string[] };
